import React from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * Unified page header used across admin/tenant pages.
 *
 * Props
 * - icon         : optional <Icon /> element rendered in a yellow round badge
 * - title        : string, required
 * - description  : string|node, optional subtitle line
 * - breadcrumb   : optional array of { label, to? } items
 * - actions      : optional node rendered on the right (buttons, search...)
 * - testId       : optional data-testid prefix (default "page-header")
 *
 * Usage:
 *   <PageHeader
 *     icon={<Users />}
 *     title="Sociétés"
 *     description="Gérez vos entités juridiques"
 *     breadcrumb={[{ label: 'Admin' }, { label: 'Sociétés' }]}
 *     actions={<Button>Nouvelle</Button>}
 *   />
 */
const PageHeader = ({
  icon,
  title,
  description,
  breadcrumb,
  actions,
  testId = 'page-header',
  className = '',
}) => {
  return (
    <header
      className={`flex flex-col gap-3 mb-6 ${className}`}
      data-testid={testId}
    >
      {Array.isArray(breadcrumb) && breadcrumb.length > 0 && (
        <nav
          className="flex items-center gap-1.5 text-xs text-zinc-500"
          data-testid={`${testId}-breadcrumb`}
        >
          {breadcrumb.map((item, idx) => (
            <React.Fragment key={`bc-${idx}`}>
              {idx > 0 && <ChevronRight className="w-3 h-3" />}
              <span className={idx === breadcrumb.length - 1 ? 'text-zinc-300' : ''}>
                {item.label}
              </span>
            </React.Fragment>
          ))}
        </nav>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          {icon && (
            <div className="shrink-0 w-10 h-10 rounded-xl bg-[var(--blitzz-yellow)]/15 text-[var(--blitzz-yellow)] flex items-center justify-center">
              {React.cloneElement(icon, { className: 'w-5 h-5' })}
            </div>
          )}
          <div className="min-w-0">
            <h1
              className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100 truncate"
              data-testid={`${testId}-title`}
            >
              {title}
            </h1>
            {description && (
              <p
                className="text-sm text-zinc-400 mt-1"
                data-testid={`${testId}-description`}
              >
                {description}
              </p>
            )}
          </div>
        </div>

        {actions && (
          <div
            className="flex items-center gap-2 flex-wrap"
            data-testid={`${testId}-actions`}
          >
            {actions}
          </div>
        )}
      </div>
    </header>
  );
};

export default PageHeader;
export { PageHeader };
